import {useLazyQuery, useMutation, useQuery} from '@apollo/client';
import {timeTrackerMutations, timeTrackerQueries} from 'api';
import {useAlert} from 'hooks/useAlert';
import {useCursorPaginationQuery} from 'hooks/useCommon';
import {
    ResponseViewModel,
    TimeTrackerCreateUpdateRequest,
    TimeTrackerListingPaginationResponse,
    TimeTrackerListingRequest,
    TimeTrackerModel,
    TimeTrackerResponse,
} from 'models';
import {useEffect} from 'react';

export const useUpdateTimeTracker = () => {
    const {invokeSuccessMessage} = useAlert();
    const [update, {loading, data}] = useMutation<
        {updateTimeTracker: TimeTrackerResponse},
        {id: string; input: TimeTrackerCreateUpdateRequest}
    >(timeTrackerMutations.UPDATE_TIME_TRACKER_ENTITY);
    useEffect(() => {
        if (data && !loading) {
            invokeSuccessMessage('Note updated successfully');
        }
    }, [data, loading]);
    return {
        updateTimeTrackerEntity: update,
        updateTimeTrackerEntityLoading: loading,
        updateTimeTrackerEntityResonse: data?.updateTimeTracker,
    };
};

export const useGetAlltimeTrackerEntities = () => {
    const {isLoading, fetchPaginated, upsertItemState, data} = useCursorPaginationQuery<
        TimeTrackerModel, // TItem
        TimeTrackerListingPaginationResponse, // TResponse
        TimeTrackerListingRequest, // TVariables
        'allTimeTrackers' // TKey (key inside response)
    >(timeTrackerQueries.GET_ALL_TIME_TRACKER_ENTITIES, 'allTimeTrackers');
    return {
        getAllTimeTrackerEntitiesLoading: isLoading,
        getAllTimeTrackerEntitiesResponse: data,
        getAllTimeTrackerEntities: fetchPaginated,
        afterCreateUpdateHandler: upsertItemState,
    };
};
export const useGetAllTimeTrackerEntitiesWithoutPagination = () => {
    const [refetch, {loading, data}] = useLazyQuery<
        {allTimeTrackersWithoutPagination: TimeTrackerModel[]},
        TimeTrackerListingRequest
    >(timeTrackerQueries.GET_ALL_TIME_TRACKER_ENTITIES_WITH_OUT_PAGINATION);

    return {
        getAllTimeTrackerEntitiesWithoutPagination: refetch,
        getAllTimeTrackerEntitiesWithoutPaginationLoading: loading,
        getAllTimeTrackerEntitiesWithoutPaginationResponse: data?.allTimeTrackersWithoutPagination,
    };
};

export const useCreateTimeTrackerEntity = () => {
    const [create, {data, loading}] = useMutation<
        {createTimeTracker: TimeTrackerResponse},
        {input: TimeTrackerCreateUpdateRequest}
    >(timeTrackerMutations.CREATE_TIME_TRACKER_ENTITY);

    return {
        createTimeTrackerEntity: create,
        createTimeTrackerEntityLoading: loading,
        createTimeTrackerEntityResponse: data?.createTimeTracker,
    };
};
export const useGetOneTimeTrackerEntry = () => {
    const [refetch, {loading, data}] = useLazyQuery<
        {oneTimeTrackerEntity: TimeTrackerModel},
        TimeTrackerListingRequest
    >(timeTrackerQueries.GET_ONE_TRACKER_ENTITY_BY_ID);

    return {
        getOneTimeTrackerEntry: refetch,
        getOneTimeTrackerEntryLoading: loading,
        getOneTimeTrackerEntryResponse: data?.oneTimeTrackerEntity,
    };
};

export const useDeleteTimeTrackerEntity = () => {
    const [deleteTimeTracker, {data, loading}] = useMutation<
        {deleteTimeEntries: ResponseViewModel<string>},
        {id: string}
    >(timeTrackerMutations.REMOVE_TIME_TRACKER_ENTITY);

    return {
        deleteTimeTrackerEntity: deleteTimeTracker,
        deleteTimeTrackerEntityLoading: loading,
        deleteTimeTrackerEntityResponse: data?.deleteTimeEntries,
    };
};

export const useStopTimeTrackerEntity = () => {
    const {invokeSuccessMessage} = useAlert();
    const [stopTimeTracker, {data, loading}] = useMutation<{stopTimeEntry: TimeTrackerResponse}, {id: string}>(
        timeTrackerMutations.STOP_TIME_TRACKER_ENTITY,
        {
            fetchPolicy: 'no-cache',
        },
    );

    useEffect(() => {
        if (!loading && data) {
            invokeSuccessMessage('Time tracker stopped successfully');
        }
    }, [data, loading]);

    return {
        stopTimeTrackerEntity: stopTimeTracker,
        stopTimeTrackerEntityLoading: loading,
        stopTimeTrackerEntityResponse: data?.stopTimeEntry,
    };
};
